import {
  useRef,
  useMemo,
  useEffect,
  useImperativeHandle,
  useCallback,
} from 'react';
import {
  FocusScope,
  useFocusManager,
  useFocusWithin,
  useInteractOutside,
  isElementInChildOfActiveScope,
} from '@sail/react-aria';
import * as React from 'react';
import {
  useFloatingNodeId,
  useInteractions,
  useRole,
  useDismiss,
  useClick,
  useFocus,
  useHover,
  safePolygon,
  FloatingNode,
  useFloatingParentNodeId,
  FloatingTree,
} from '@floating-ui/react';
import type {
  ElementProps,
  FloatingContext,
  Placement as FloatingPlacement,
} from '@floating-ui/react';
import {
  createView,
  view,
  assignProps,
  dynamic,
  animate,
  AnimatePresence,
  toIntent,
} from '@sail/engine';
import type {FocusManager} from '@sail/react-aria';
import {tokens} from '@sail/tokens';
import {css} from '../../css';
import {Layer} from '../layers/Layer';
import {toFloatingPlacement} from '../utils/toFloatingPlacement';
import {useProps} from '../../hooks/useProps';
import type {PopoverInternalProps} from './utils';
import {moveFocus, FocusGuard} from './utils';
import {usePopoverIframe} from '../../iframe-overrides/PopoverOverrides';
import {useUiConfig} from '../../providers/UiConfigProvider';
import {useFloating} from './useFloating';
/* eslint-disable @stripe-internal/stripe/hooks-in-createview */

const iframeOverrides = toIntent([
  assignProps(usePopoverIframe),
  assignProps({dismissible: false}),
  css({
    backgroundColor: 'surface',
    border: '1px solid',
    borderColor: 'border',
    borderRadius: 'medium',
    boxShadow: 'overlay',
    maxHeight: 'inherit',
  }),
]);

/**
 * Popover option props have either a boolean value, or an object with options.
 * This helper function returns a tuple of the boolean value indicating whether the option is enabled,
 * and the option object itself, defaulting to `{}` if not provided.
 */
const option = <T,>(
  value: boolean | T | undefined,
): [boolean | undefined, T] => {
  if (value === undefined) {
    return [undefined, {} as T];
  }
  if (typeof value === 'boolean') {
    return [value, {} as T];
  }
  return [true, value];
};

/**
 * A portal-friendly version of react-aria's `useInteractOutside` hook, allowing
 * the popover to be dismissed when focus moves outside the popover.
 *
 * `useInteractOutside` uses DOM event delegation to detect clicks outside the popover,
 * but this isn't safe for portalled elements because the event target may not be
 * a direct child of the popover.
 *
 * This hook uses the React tree (instead of the DOM tree) to detect when focus moves
 * outside the popover.
 */
const useFocusOutside = (
  callback: () => void,
  targetElement?: HTMLElement | null,
): Pick<React.HTMLProps<Element>, 'onFocus' | 'onBlur'> => {
  const focusMovedRef = useRef(false);
  const timeoutRef = React.useRef<number>();
  useEffect(() => () => cancelAnimationFrame(timeoutRef.current as number), []);

  return useMemo(
    () => ({
      onFocus() {
        focusMovedRef.current = true;
      },
      // blur on the current element fires before focus on the next element,
      // so we use that to determine if the focus is moving outside the popover
      onBlur(e) {
        focusMovedRef.current = false;

        // Ignore blur events default prevented by children
        if (e.defaultPrevented) {
          return;
        }

        cancelAnimationFrame(timeoutRef.current as number);

        const relatedTarget = e.relatedTarget as HTMLElement;

        const isFocusWithinCurrentTarget =
          e.currentTarget?.contains(relatedTarget) ?? false;

        /**
         * In some cases trigger element might not be inside the current target
         */
        const isFocusWithinTargetElement =
          targetElement?.contains(relatedTarget) ?? false;

        const isFocusWithin =
          isFocusWithinCurrentTarget || isFocusWithinTargetElement;

        /**
         * preventDefault onBlur event if focus is within targeted elements,
         * so parent Popovers don't close on internal focus changes
         */
        if (isFocusWithin) {
          e.preventDefault();
        }

        // Give browser time to focus the next element
        timeoutRef.current = requestAnimationFrame(() => {
          // Check if the new focused element is a child of the original container
          if (!focusMovedRef.current && !isFocusWithin) {
            callback();
          }
        });
      },
    }),
    [callback, targetElement],
  );
};

const useReactAriaFocusOutside = (
  context: FloatingContext<HTMLDivElement>,
  {enabled = false}: {enabled?: boolean},
): ElementProps => {
  const {
    refs,
    open: isOpen,
    onOpenChange,
    elements: {domReference, floating},
  } = context;

  const isElementWithin = useCallback(
    (element: Element) =>
      domReference?.contains(element) || floating?.contains(element),
    [domReference, floating],
  );

  /**
   * This ref keeps track of whether focus is within the overlay or not.
   * It needs to prevent the overlay from closing when focus moves to a child focus scope.
   * Example: Popover -> Popover -> Button, moving focus to Button should not close the popover.
   */
  const isFocusWithinRef = useRef(false);
  const timeoutRef = useRef<number>();
  useEffect(() => () => cancelAnimationFrame(timeoutRef.current as number), []);

  const onInteractOutside = useCallback(
    (e: React.SyntheticEvent) => {
      /**
       * Do not close the overlay if focus is within the overlay.
       */
      if (isFocusWithinRef.current) {
        return;
      }

      const isElementWithinPopover = isElementWithin(e.target as Element);

      if (!isElementWithinPopover) {
        onOpenChange(false, e.nativeEvent);
      }
    },
    [isElementWithin, onOpenChange],
  );

  // Handle clicking outside the overlay to close it
  useInteractOutside({
    ref: refs.floating,
    onInteractOutside: enabled && isOpen ? onInteractOutside : undefined,
  });

  const {focusWithinProps} = useFocusWithin({
    isDisabled: !enabled,
    onFocusWithinChange: (isFocusWithin) => {
      isFocusWithinRef.current = isFocusWithin;
    },
    onBlurWithin: (e) => {
      const relatedTarget = e.relatedTarget as Element | null;
      // Do not close if relatedTarget is null, which means focus is lost to the body.
      // That can happen when switching tabs, or due to a VoiceOver/Chrome bug with Control+Option+Arrow navigation.
      // Clicking on the body to close the overlay should already be handled by useInteractOutside.
      // https://github.com/adobe/react-spectrum/issues/4130
      // https://github.com/adobe/react-spectrum/issues/4922
      //
      // If focus is moving into a child focus scope (e.g. menu inside a dialog),
      // do not close the outer overlay. At this point, the active scope should
      // still be the outer overlay, since blur events run before focus.
      if (
        !relatedTarget ||
        relatedTarget === document.body ||
        isElementInChildOfActiveScope(relatedTarget)
      ) {
        return;
      }

      isFocusWithinRef.current = false;

      cancelAnimationFrame(timeoutRef.current as number);

      const isRelatedTargetWithin = isElementWithin(relatedTarget);

      timeoutRef.current = requestAnimationFrame(() => {
        const isFocusWithin = isFocusWithinRef.current;

        if (!isFocusWithin && !isRelatedTargetWithin) {
          onOpenChange(false, e.nativeEvent);
        }
      });
    },
  });

  return useMemo(() => {
    if (!enabled || !isOpen) {
      return {};
    }

    return {
      floating: focusWithinProps,
      reference: focusWithinProps,
    };
  }, [enabled, isOpen, focusWithinProps]);
};

const translate = (placement: FloatingPlacement) => {
  /* eslint-disable no-nested-ternary */
  const [attachedSide] = placement.split('-');
  const offset = tokens['space.150'];
  const negativeOffset = `calc(-1 * ${offset})`;
  return `translate(${
    attachedSide === 'left'
      ? offset
      : attachedSide === 'right'
      ? negativeOffset
      : '0'
  }, ${
    attachedSide === 'top'
      ? offset
      : attachedSide === 'bottom'
      ? negativeOffset
      : '0'
  })`;
};

const ANIMATION_EASING = 'cubic-bezier(0, .09, .4, 1)';
const ANIMATION_DURATION_MEDIUM = 150;
const ANIMATION_DURATION_SHORT = 100;
const POPOVER_PADDING = 4;

const DesktopPopoverLayer = createView(
  ({
    popoverIsControlled,
    open,
    onOpenChange,
    targetRef,
    placement: reactAriaPlacement,
    offset: offsetOptions,
    flip,
    shift: shouldShift,
    size: sizeOptions,
    inline: inlineEnabled,
    hide: hideOptions,
    subviews,
    dismissible,
    showOnPress,
    showOnFocus,
    showOnHover,
    role,
    bottomSheetTitle,
    floatingStrategy,
    animate: shouldAnimate = true,
    disableDefaultBottomSheetScrollWrapper,
    useReactAriaDismissOutside = false,
    disableDefaultBottomSheetHeader,
    bottomSheetAlwaysUseMaxHeight,
    bottomSheetVariant,
    ...props
  }: PopoverInternalProps) => {
    const placement = toFloatingPlacement(reactAriaPlacement);

    const [shiftEnabled, shiftOptions] = option(shouldShift);
    // eslint-disable-next-line prefer-const
    let [dismissEnabled, dismissOptions] = option(dismissible);
    const [clickEnabled, clickOptions] = option(showOnPress);
    const [focusEnabled, focusOptions] = option(showOnFocus);
    const [hoverEnabled, hoverOptions] = option(showOnHover);
    const [flipEnabled, flipOptions] = option(flip);

    const nodeId = useFloatingNodeId();

    const floating = useFloating<HTMLDivElement>({
      nodeId,
      placement,
      floatingStrategy,
      offsetValue: offsetOptions ?? POPOVER_PADDING,
      inlineEnabled,
      flipEnabled,
      flipOptions,
      hideOptions,
      shiftEnabled: shiftEnabled !== false,
      shiftOptions,
      sizeOptions,
      open,
      onOpenChange,
    });

    const {refs, floatingStyles, context, middlewareData} = floating;

    const scope = useRef<FocusManager>(null);

    const shouldDismissOnFocusOutside =
      dismissEnabled && dismissOptions.outsideFocus !== false;

    const dismissOnFocusOutside = useFocusOutside(
      useCallback(() => {
        if (!useReactAriaDismissOutside && shouldDismissOnFocusOutside) {
          onOpenChange(false);
        }
      }, [
        onOpenChange,
        shouldDismissOnFocusOutside,
        useReactAriaDismissOutside,
      ]),
      targetRef.current,
    );

    const interactions = useInteractions([
      useRole(context, {
        enabled: Boolean(role),
        role,
      }),
      useDismiss(context, {
        enabled: dismissEnabled,
        escapeKey: dismissOptions.escapeKey,
        outsidePress: dismissOptions.outsidePress,
        ancestorScroll: dismissOptions.ancestorScroll ?? false,
        // outsidePressEvent needs to be set to 'click' to ensure compatibility with
        // react-aria's Pressable implementation.
        outsidePressEvent: 'click',
        bubbles: dismissOptions.bubbles,
      }),
      useClick(context, {
        enabled: clickEnabled,
        toggle: clickOptions.toggle,
      }),
      useFocus(context, {
        enabled: focusEnabled,
        keyboardOnly: focusOptions.visibleOnly,
      }),
      useHover(context, {
        enabled: hoverEnabled,
        delay: hoverOptions.delay,
        restMs: hoverOptions.restMs,
        handleClose: safePolygon(),
      }),
      // wire up the reference element to dismiss the popover when it loses focus.
      // This logic is only necessary because the `useDismiss` hook relies on
      // event delegation and react-aria "pressable" elements don't propagate mouse/touch events.
      {reference: dismissOnFocusOutside, floating: dismissOnFocusOutside},
      useReactAriaFocusOutside(context, {
        enabled: useReactAriaDismissOutside && shouldDismissOnFocusOutside,
      }),
    ]);

    useProps(targetRef, interactions.getReferenceProps());

    // set up refs for the trigger element for floating-ui
    useEffect(() => {
      // floating UI will call `calculatePosition` as long as it has both a floating and reference element
      // we can clear this reference (to avoid calculating position) if switching to mobile
      refs.setReference(targetRef.current);
    }, [targetRef, refs]);

    const ref = React.useRef<HTMLDivElement>(null);

    const layer = (
      <Layer
        inherits={subviews.layer}
        uses={[
          assignProps(
            interactions.getFloatingProps() as React.HTMLAttributes<HTMLDivElement>,
          ),
        ]}
        ref={(el: HTMLDivElement) => {
          // don't run positioning logic within JSDOM environment (speeds up tests)
          // JSDOM doesn't support `getBoundingClientRect` anyway, so positioning logic
          // wouldn't be correct there anyway.
          if (typeof jest === 'undefined') {
            refs.setFloating(el);
          }
        }}
        style={floatingStyles}
        css={
          hideOptions?.referenceHidden && middlewareData.hide?.referenceHidden
            ? css({visibility: 'hidden'})
            : undefined
        }
      >
        {/* used to coordinate nested popovers */}
        <FloatingNode id={nodeId}>
          <FocusScope restoreFocus>
            <view.div
              // declare this before spreading props so user's test id wins out, but we can
              // still use this test id for sail next tests
              data-testid="popover-layer"
              {...props}
              css={sizeOptions?.maxHeight ? css({overflow: 'auto'}) : undefined}
              ref={ref}
              uses={[
                dynamic(() => {
                  const manager = useFocusManager();
                  useImperativeHandle(scope, () => manager);
                }),
                ...(shouldAnimate
                  ? [
                      animate.in({
                        opacity: 0,
                        transform: `scale(0.95) ${translate(
                          floating.placement,
                        )}`,
                        easing: ANIMATION_EASING,
                        duration: ANIMATION_DURATION_MEDIUM,
                      }),
                      animate.out({
                        opacity: 0,
                        transform: `scale(0.95) ${translate(
                          floating.placement,
                        )}`,
                        easing: ANIMATION_EASING,
                        duration: ANIMATION_DURATION_SHORT,
                      }),
                    ]
                  : []),
                css({maxHeight: 'inherit'}),
              ]}
            >
              {props.children}
            </view.div>
          </FocusScope>
        </FloatingNode>
      </Layer>
    );

    return (
      <>
        {/* focus guards to allow focus to move between the page and the popover */}
        {open ? <FocusGuard onFocus={moveFocus(scope, 'next')} /> : null}
        <AnimatePresence>{context.open ? layer : null}</AnimatePresence>
        {open ? <FocusGuard onFocus={moveFocus(scope, 'prev')} /> : null}
      </>
    );
  },
  {
    css: {
      backgroundColor: 'surface',
      border: '1px solid',
      borderColor: 'border',
      borderRadius: 'medium',
      boxShadow: 'overlay',
      maxHeight: 'inherit',
    },
  },
);

export const DesktopPopover = createView<PopoverInternalProps>((props) => {
  const uiConfig = useUiConfig();
  const parentId = useFloatingParentNodeId();
  const shouldApplyIframeOverrides = !!uiConfig?._unstable_requestIframe;

  const intents = shouldApplyIframeOverrides ? iframeOverrides : undefined;

  const popover = <DesktopPopoverLayer {...props} uses={intents} />;

  // If there is no ancestor popover, wrap the popover in a FloatingTree
  // so that nested popovers can coordinate dismissal behavior.
  return parentId === null ? <FloatingTree>{popover}</FloatingTree> : popover;
});
